import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { lmsAdmin } from '../api'
import { logoFor, domainOf, CONNECTION_STATUS_PILL, restrictWarning } from '../format'
import { LmsIcon } from '../lms-icon'

// Add LMS Integration modal (design edxr-add-lms-modal-v1.0.html, opened
// from Course → LMS Integration's "+ Add LMS"). Artifact states: picker,
// selected (summary visible), conflicts (Already added), empty. Decision #9:
// the mapping flow must surface the course's access level and offer the
// one-click private→only assigned restriction — mapping never silently
// changes access levels.
//
// An LTI 1.3 connection (Phase L Task 8) has no package: adding a course maps
// it and builds nothing, and the summary says how the LMS's activity reaches it
// (Deep Linking's "Select content", or the edxr_course custom parameter).

export const AddIntegrationModal = ({ course, mappedConnectionIds, onClose, onAdded }) => {
  const history = useHistory()

  const [connections, updateConnections] = useState(null)
  const [selected, updateSelected] = useState(null)
  const [restrict, updateRestrict] = useState(false)
  const [error, updateError] = useState(null)
  const [submitting, updateSubmitting] = useState(false)
  // Set once the course is connected but something after the map did not go
  // as asked: { restrict: the restrict warning, build: the launcher build's
  // failure }. The modal then stays on it until the admin has read it.
  const [warning, updateWarning] = useState(null)

  useEffect(() => {
    lmsAdmin
      .listConnections()
      .then(result => updateConnections((result.connections || []).filter(c => c.status !== 'archived')))
      .catch(updateError)
  }, [])

  const available = (connections || []).filter(c => !mappedConnectionIds.includes(c.id))

  // An xAPI record-store connection has no LMS learners to keep access, so
  // making the course only assigned would be wrong for it.
  // an xAPI record store has no LMS learners, and an archived connection launches nobody
  const canRestrict = !!selected && selected.delivery !== 'xapi-lrs' && selected.status !== 'archived'
  // an LTI 1.3 connection launches from the LMS's own activity: nothing to build
  const isLti = !!selected && selected.delivery === 'lti13'

  const submit = async () => {
    if (!selected || submitting) return
    updateSubmitting(true)
    let mapping
    try {
      mapping = await lmsAdmin.mapCourse(selected.id, {
        node_id: Number(course.node_id),
        restrict_to_lms_learners: restrict && canRestrict
      })
    } catch (e) {
      updateError(e)
      updateSubmitting(false)
      return
    }
    // The course is connected now, and the restrict decided. A failed build
    // must not hide either (Task 16 carry): the admin is told both, and the
    // page's Build button finishes the launcher.
    const notRestricted = restrictWarning(mapping)
    let buildFailure = null
    if (!isLti) {
      try {
        await lmsAdmin.buildPackage(selected.id, Number(course.node_id))
      } catch (e) {
        buildFailure = e.message || 'The launcher could not be assembled.'
      }
    }
    if (notRestricted || buildFailure) {
      updateWarning({ restrict: notRestricted, build: buildFailure, connectionName: selected.name })
      updateSubmitting(false)
      return
    }
    try {
      await onAdded()
    } catch (e) {
      updateError(e)
      updateSubmitting(false)
    }
  }

  return (
    <div className="lms-modal-scrim" data-testid="add-integration-modal">
      <div className="lms-modal">
        <div className="modal-head">
          <div className="icon">
            <LmsIcon name="link" />
          </div>
          <div className="body">
            <h2>Add LMS Integration</h2>
            <p className="target">
              to <span className="target-name">{course.name}</span>
            </p>
          </div>
          <button className="close-btn" title="Close" type="button" onClick={warning ? onAdded : onClose}>
            <LmsIcon name="xmark" />
          </button>
        </div>

        <div className="lms-modal-body">
          {error && (
            <div className="alert alert-danger" role="alert" data-testid="modal-error">
              {error.message}
            </div>
          )}

          {warning && warning.build && (
            <div className="alert alert-danger" role="alert" data-testid="build-failed">
              The course is connected to {warning.connectionName}, but its launcher did not build: {warning.build}{' '}
              Build it from this page with Build package on its row.
            </div>
          )}

          {warning && warning.restrict && (
            <div className="alert alert-warning" role="alert" data-testid="restrict-warning">
              {warning.restrict}
            </div>
          )}

          {!warning && connections && connections.length === 0 && (
            <div className="lms-zero">
              <div className="zero-icon">
                <LmsIcon name="link" />
              </div>
              <h3>No LMS connections yet</h3>
              <p>
                Set up your first LMS connection to start generating SCORM packages for courses. You'll only need to do
                this once per LMS.
              </p>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                onClick={() => history.push('/org/lms/connections/new')}
              >
                <LmsIcon name="plus" className="me-1" />
                Set up first connection
              </button>
            </div>
          )}

          {!warning && connections && connections.length > 0 && (
            <React.Fragment>
              <div className="section-head">
                <h3>Choose an LMS connection</h3>
              </div>
              {connections.map(connection => {
                const logo = logoFor(connection.preset && connection.preset.name)
                const added = mappedConnectionIds.includes(connection.id)
                const pill = CONNECTION_STATUS_PILL[connection.status] || CONNECTION_STATUS_PILL.archived
                const isSelected = selected && selected.id === connection.id
                return (
                  <div
                    key={connection.id}
                    className={`picker-card ${isSelected ? 'selected' : ''} ${added ? 'disabled' : ''}`}
                    data-testid={`picker-${connection.id}`}
                    onClick={() => {
                      if (!added) updateSelected(connection)
                    }}
                  >
                    <span className="picker-radio" />
                    <div className={`conn-logo ${logo.className}`}>{logo.text}</div>
                    <div className="picker-body">
                      <div className="name-row">
                        <strong>{connection.name}</strong>
                        {connection.delivery === 'lti13' && <span className="pill info ms-2">LTI 1.3</span>}
                      </div>
                      <div className="domain">{domainOf(connection.lms_base_url)}</div>
                    </div>
                    <div className="picker-meta">
                      {added ? (
                        <span className="pill added">
                          <LmsIcon name="check" />
                          Already added
                        </span>
                      ) : (
                        <span className={`pill ${pill.className}`}>
                          <span className="dot" />
                          {pill.label}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
              <button
                className="btn btn-light border btn-sm"
                type="button"
                onClick={() => history.push('/org/lms/connections/new')}
              >
                <LmsIcon name="plus" className="me-1" />
                Set up a new connection
              </button>

              {selected && (
                <React.Fragment>
                  {course.access_level === 'private' && (
                    <div className="visibility-notice" data-testid="visibility-notice">
                      This course is <strong>private</strong> — all org users can already see this, including LMS
                      learners.
                      {canRestrict && (
                        <label className="restrict-row">
                          <input
                            type="checkbox"
                            checked={restrict}
                            data-testid="restrict-checkbox"
                            onChange={e => updateRestrict(e.target.checked)}
                          />
                          Only LMS learners: make this course only assigned. People in this organization who are not
                          assigned lose it; the connection's learners keep it.
                        </label>
                      )}
                    </div>
                  )}
                  {course.access_level === 'assigned' && (
                    <div className="visibility-notice" data-testid="visibility-notice">
                      This course is only for people assigned to it.
                      {/* An xAPI record store has no LMS learners to assign through a group. */}
                      {selected.delivery !== 'xapi-lrs' &&
                        " The connection's learners are assigned through its group."}
                    </div>
                  )}

                  <div className="section-head" style={{ marginTop: 16 }}>
                    <h3>What will happen</h3>
                  </div>
                  <div className="summary-block" data-testid="summary-block">
                    {isLti ? (
                      <ul>
                        <li className="positive">
                          <LmsIcon name="check" />
                          <span>
                            The course is connected to <strong>{selected.name}</strong> at once. There is no package to build
                            or upload.
                          </span>
                        </li>
                        <li className="positive">
                          <LmsIcon name="link" />
                          <span>
                            In your LMS, add an <strong>external tool</strong> activity for EducationXR and choose{' '}
                            <strong>Select content</strong>: your LMS lists the connected courses and fills the course in
                            for you (Deep Linking).
                          </span>
                        </li>
                        <li className="neutral">
                          <LmsIcon name="id-badge" />
                          <span>
                            Or add the custom parameter <code>edxr_course=&lt;course ID&gt;</code> to the activity by hand.
                            This page shows the line once the course is added.
                          </span>
                        </li>
                        <li className="neutral">
                          <LmsIcon name="shield-halved" />
                          <span>
                            Learners open the course from your LMS, which signs who is launching: the launch is verified by
                            the LMS.
                          </span>
                        </li>
                      </ul>
                    ) : (
                      <ul>
                        <li className="positive">
                          <LmsIcon name="check" />
                          <span>
                            A small SCORM dispatch launcher (~50KB) will be assembled for <strong>{selected.name}</strong>.
                            Typically completes in 1-3 seconds.
                          </span>
                        </li>
                        <li className="positive">
                          <LmsIcon name="link" />
                          <span>
                            When a learner starts the course in your LMS, <strong>EducationXR opens in a new tab</strong>. The
                            learner signs in to EducationXR there when they need to. The connection's settings decide whether
                            that is every launch.
                          </span>
                        </li>
                        <li className="neutral">
                          <LmsIcon name="id-badge" />
                          <span>
                            The first time, the learner confirms that their LMS account belongs to their EducationXR account.
                            Confirming the link makes the learner a member of your organization. Launching never creates an
                            EducationXR account: a learner without one signs up first.
                          </span>
                        </li>
                        <li className="neutral">
                          <LmsIcon name="shield-halved" />
                          <span>
                            The launch gives the learner a short, limited session that ends on its own. It is not a full
                            EducationXR sign-in.
                          </span>
                        </li>
                        <li className="neutral">
                          <LmsIcon name="globe" />
                          <span>
                            The course then opens in the EducationXR <strong>web viewer</strong>. A course with no web version
                            opens in the app instead.
                          </span>
                        </li>
                        <li className="neutral">
                          <LmsIcon name="rotate" />
                          <span>
                            When the learner finishes, the <strong>result goes back to your LMS</strong>. Learners keep the LMS
                            tab open until they finish.
                          </span>
                        </li>
                      </ul>
                    )}
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </div>

        <div className="modal-foot">
          {warning && (
            <button
              className="btn btn-primary btn-sm"
              type="button"
              data-testid="modal-done"
              style={{ marginLeft: 'auto' }}
              onClick={onAdded}
            >
              Done
            </button>
          )}
          {!warning && connections && connections.length > 0 && (
            <span className="help">
              {selected ? (
                <React.Fragment>
                  <LmsIcon name={isLti ? 'plug' : 'clock'} regular={!isLti} />
                  {isLti ? 'Nothing to build' : 'Assembly takes 1-3 seconds'}
                </React.Fragment>
              ) : available.length === 0 && connections.length > 0 ? (
                <React.Fragment>
                  <LmsIcon name="circle-info" />
                  All connections already have this course
                </React.Fragment>
              ) : null}
            </span>
          )}
          {!warning && (
            <button className="btn btn-light border btn-sm" type="button" onClick={onClose} style={connections && connections.length === 0 ? { marginLeft: 'auto' } : undefined}>
              {connections && connections.length === 0 ? 'Close' : 'Cancel'}
            </button>
          )}
          {!warning && connections && connections.length > 0 && (
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={!selected || submitting}
              data-testid="modal-add"
              onClick={submit}
            >
              <LmsIcon name="plus" className="me-1" />
              {selected ? (isLti ? 'Add course' : 'Add and assemble launcher') : 'Add LMS Integration'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
